import { createApiService, API_CONFIGS } from '../utils/apiService';
import axios, { deduplicatedInstance } from "@configs/axios";
import i18next from "i18next";

const operations = {
  // Centralized function for fetching wallet list - used by both Redux and React Query
  fetchWalletList: createApiService(
    () => deduplicatedInstance.get("/user_wallets/list"),
    {
      ...API_CONFIGS.GET_WITH_DATA,
      context: 'Wallet - Fetch List',
      transformResponse: (data) => data.data.data // Return the exact data structure needed
    }
  ),

  getMyWalletList: createApiService(
    () => deduplicatedInstance.get("/user_wallets/list"),
    {
      ...API_CONFIGS.GET_WITH_DATA,
      context: 'Wallet - Get My List',
      transformResponse: (data) => data.data
    }
  ),
  getTotal: createApiService(
    () => axios.get("/assets"),
    {
      ...API_CONFIGS.GET_WITH_DATA,
      context: 'Wallet - Get Total Assets',
      transformResponse: (data) => data.data
    }
  ),

  getTransactionLimits: createApiService(
    () => axios.get("/user_wallets/limits"),
    {
      ...API_CONFIGS.GET_WITH_DATA,
      context: 'Wallet - Get Transaction Limits',
      transformResponse: (data) => data.data
    }
  ),

  getMorWalletList: createApiService(
    () => axios.get("/exchange_list"),
    {
      ...API_CONFIGS.GET_WITH_DATA,
      context: 'Wallet - Get Exchange List',
      transformResponse: (data) => data.data
    }
  ),
  fetchDepositAddress: async (symbol, depositNetwork) => {
    const data = { symbol, smart_contract_name: depositNetwork };
    try {
      const res = await axios.post("/user_wallets/getdepositaddress", { ...data }, {});
      if (res.data) {
        return res.data;
      } else {
        throw "no data";
      }
    } catch (err) {
      throw err;
    }
  },
  manualDeposit: async (transactionCode, symbol, amount, depositNetwork, lang, chain) => {
    const data = {
      transaction_code: transactionCode,
      exchange_symbol: symbol,
      amount,
      smart_contract_name: depositNetwork,
      chain: depositNetwork,
    };
    try {
      const res = await axios.post(
        "/user_wallets/manual_deposit",
        { ...data },
        {
          headers: {
            "x-lang": `${lang}`, // Replace 'your-lang-value' with the actual value you want to send
          },
        }
      );
      if (res.data) {
        return res.data;
      } else {
        throw "no data";
      }
    } catch (err) {
      throw err;
    }
  },
  manualDepositReialHistory: async (lang) => {
    try {
      const res = await axios.get("/tomanWallet/offline_deposit/history", {
        headers: {
          "x-lang": `${lang}`, // Replace 'your-lang-value' with the actual value you want to send
        },
      });
      if (res.data) {
        return res.data;
      } else {
        throw "no data";
      }
    } catch (err) {
      throw err;
    }
  },
  autoDeposit: async (transactionCode, lang) => {
    const data = { transaction_code: transactionCode };
    try {
      const res = await axios.post(
        "/deposit/automatic_deposit",
        { ...data },
        {
          headers: {
            "x-lang": `${lang}`, // Replace 'your-lang-value' with the actual value you want to send
          },
        }
      );
      if (res.data) {
        return res.data;
      } else {
        throw "no data";
      }
    } catch (err) {
      throw err;
    }
  },
  /**
   * Cryptocurrency Withdrawal API - Two-Step Flow
   *
   * Step 1: Submit without verification code
   *   - Backend sends SMS/requires Google Auth
   *   - Returns: { verification_type: "sms" | "google_authenticator", next_step: "send_verification_code" }
   *
   * Step 2: Submit with verification code
   *   - Backend validates code and processes withdrawal via external wallet service
   *   - Returns: { id, transaction_id, total_amount, fee, amount, status: "Pending" }
   *
   * @param {number} amount - Withdrawal amount
   * @param {string} walletAddress - Destination wallet address (26-65 chars)
   * @param {string|null} tag - Memo/Tag for certain cryptocurrencies (XRP, XLM, etc.)
   * @param {string} symbol - Cryptocurrency symbol (BTC, ETH, USDT, etc.)
   * @param {string} chain - Network/blockchain name (TRC20, Ethereum, BEP20, etc.)
   * @param {string} lang - Language code (en or fa)
   * @param {string|null} activation_code - SMS verification code (5 digits) - Optional for step 1
   * @param {string|null} secret_key - Google Authenticator code (6 digits) - Optional for step 1
   * @returns {Promise} API response
   */
  manualWithdraw: async (amount, walletAddress, tag, symbol, chain, lang, activation_code = null, secret_key = null) => {
    const data = {
      amount,
      wallet_address: walletAddress,
      tag,
      symbol: symbol,
      chain,
    };

    // Add verification code if provided (Step 2)
    if (activation_code) {
      data.activation_code = activation_code;
    }
    if (secret_key) {
      data.secret_key = secret_key;
    }

    try {
      console.log('[MyWallet.manualWithdraw] Sending request:', data);
      const res = await axios.post(
        "/user_wallets/manual_withdraw",
        { ...data },
        {
          headers: {
            "x-lang": `${lang}`,
          },
        }
      );
      console.log('[MyWallet.manualWithdraw] Response received:', res.data);

      if (res.data) {
        return res.data;
      } else {
        console.error('[MyWallet.manualWithdraw] No data in response');
        throw new Error("no data");
      }
    } catch (err) {
      console.error('[MyWallet.manualWithdraw] Error caught:', err);
      console.error('[MyWallet.manualWithdraw] Error response:', err?.response?.data);
      // Re-throw the error to be handled by the caller
      throw err;
    }
  },
  shetabDeposit: async (cartId, amount, lang, reserve_payment = true) => {
    const data = { card_id: cartId, amount, reserve_payment };
    try {
      const res = await axios.post(
        "/tomanWallet/charge",
        { ...data },
        {
          headers: {
            "x-lang": `${lang}`, // Replace 'your-lang-value' with the actual value you want to send
          },
        }
      );
      if (res.data) {
        return res.data;
      } else {
        throw "no data";
      }
    } catch (err) {
      throw err;
    }
  },
  /**
   * Toman Withdrawal API - Two-Step Flow
   *
   * Step 1: Submit without verification code
   *   - Backend sends SMS/requires Google Auth
   *   - Returns: { verification_type: "sms" | "google_authenticator", next_step: "send_verification_code" }
   *
   * Step 2: Submit with verification code
   *   - Backend validates code and creates withdrawal
   *   - Returns: { id, trans_id, status: "0" (pending) }
   *
   * @param {number} cartId - Bank card ID
   * @param {number} amount - Withdrawal amount in Tomans
   * @param {string|null} activation_code - SMS verification code (5 digits) - Optional for step 1
   * @param {string|null} secret_key - Google Authenticator code (6 digits) - Optional for step 1
   * @returns {Promise} API response
   */
  shetabWithDraw: async (cartId, amount, activation_code = null, secret_key = null) => {
    const data = { card_id: cartId, amount };

    // Add verification code if provided (Step 2)
    if (activation_code) {
      data.activation_code = activation_code;
    }
    if (secret_key) {
      data.secret_key = secret_key;
    }

    const lang = i18next.language === "en" ? "en" : "fa";
    try {
      const res = await axios.post(
        "/user_wallets/toman/withdraw",
        { ...data },
        {
          headers: {
            "x-lang": `${lang}`,
          },
        }
      );
      if (res.data) {
        return res.data;
      } else {
        throw "no data";
      }
    } catch (err) {
      throw err;
    }
  },

  /**
   * Resend verification code for withdrawal
   * Used when user needs a new SMS verification code during withdrawal process
   *
   * @returns {Promise} API response
   */
  resendWithdrawalCode: async () => {
    const lang = i18next.language === "en" ? "en" : "fa";
    try {
      const res = await axios.post(
        "/user_wallets/resend_code",
        {},
        {
          headers: {
            "x-lang": `${lang}`,
          },
        }
      );
      if (res.data) {
        return res.data;
      } else {
        throw "no data";
      }
    } catch (err) {
      throw err;
    }
  },

  fetchShetabWithDrawTransactions: async (page, query) => {
    const reqQuery = `?page=${page}&query=${query}`;
    try {
      const res = await axios.get(`/user_wallets/toman/withdrawlist${reqQuery}`, {});
      if (res.data) {
        return res.data.data;
      } else {
        throw "no data";
      }
    } catch (err) {
      throw err;
    }
  },
  fetchWithDrawTransactions: async (page, query) => {
    try {
      const res = await axios.get(
        `/user_wallets/withdraw_history?page=${page}${query}`,
        {}
      );
      if (res.data) {
        return res.data.data;
      } else {
        throw "no data";
      }
    } catch (err) {
      throw err;
    }
  },
  fetchDepositTransactions: async (page, query) => {
    try {
      const res = await axios.get(
        `/user_wallets/deposit_history?page=${page}${query}`,
        {}
      );
      if (res.data) {
        return res.data.data;
      } else {
        throw "no data";
      }
    } catch (err) {
      throw err;
    }
  },

  fetchInternalTransfer: async (payload) => {
    try {
      const res = await axios.post("/user_wallets/internal_transfer", payload);
      if (res.data) {
        return res.data.data;
      } else {
        throw "no data";
      }
    } catch (err) {
      throw err;
    }
  },
  fetchInternalTransferHistory: async (page, query) => {
    const reqQuery = `?page=${page}&query=${query}`;
    try {
      const res = await axios.get(
        `/user_wallets/internal_transfer_history${reqQuery}`,
        {}
      );
      if (res.data) {
        return res.data.data;
      } else {
        throw "no data";
      }
    } catch (err) {
      throw err;
    }
  },

  estimateFee: createApiService(
    (amount, network, coin, to_address) => {
      const data = { amount, network, coin, to_address };
      return axios.post("/user_wallets/estimateFee", data);
    },
    {
      ...API_CONFIGS.POST_CREATE,
      context: 'Wallet - Estimate Fee',
      transformResponse: (data) => data.data
    }
  ),

  /**
   * Get Dust Wallets
   * Fetches list of wallets with small balances (dust) that can be converted
   *
   * @returns {Promise} API response with dust wallets data
   */
  getDustWallets: createApiService(
    () => axios.get("/user_wallets/dust_wallets"),
    {
      ...API_CONFIGS.GET_WITH_DATA,
      context: 'Wallet - Get Dust Wallets',
      transformResponse: (data) => data.data
    }
  ),

  /**
   * Release Dust (Convert small balances)
   * Converts dust from selected wallets to target currency (USDT or IRT)
   *
   * @param {Array<string>} wallets - Array of wallet symbols to convert (e.g., ["TRX", "BTC"])
   * @param {string} convertTo - Target currency to convert to ("usdt" or "irt")
   * @returns {Promise} API response
   */
  releaseDust: createApiService(
    (wallets, convertTo) => {
      const data = {
        wallets,
        convert_to: convertTo
      };
      return axios.post("/user_wallets/dust/release", data);
    },
    {
      ...API_CONFIGS.POST_CREATE,
      context: 'Wallet - Release Dust',
      transformResponse: (data) => data.data
    }
  ),
};

export default operations;
